<template>
  <main
    class="offer-list-page"
    role="main"
    aria-labelledby="offers-admin-h1"
    style="padding-top: 5px;"
  >
    <header class="page-header">
      <h1
        id="offers-admin-h1"
        class="h1"
      >
        Angebote – Übersicht
      </h1>
      <div class="actions">
        <button
          class="btn btn-primary"
          aria-labelledby="offers-admin-h1"
          data-testid="offer-create"
          @click="openCreate = true"
        >
          Neu
        </button>
      </div>
    </header>

    <section class="card">
      <!-- Filterzeile -->
      <div
        class="filters filters--offers"
      >
        <div
          class="field date-picker filters__field filters__field--date"
        >
          <label for="f-date">Datum</label>
          <div class="filters__date-input">
            <button
              type="button"
              class="icon-btn"
              :title="'Vorheriger Tag'"
              :aria-label="'Vorheriger Tag'"
              @click="prevDay"
            >
              <i
                class="pi pi-angle-left"
                aria-hidden="true"
              />
            </button>
            <flat-pickr
              id="f-date"
              v-model="date"
              :config="fpOpts"
              @on-change="load"
            />
            <button
              type="button"
              class="icon-btn"
              :title="'Nächster Tag'"
              :aria-label="'Nächster Tag'"
              @click="nextDay"
            >
              <i
                class="pi pi-angle-right"
                aria-hidden="true"
              />
            </button>
          </div>
        </div>

        <div
          class="field filters__field filters__field--search"
        >
          <label for="f-q">Suche</label>
          <input
            id="f-q"
            v-model="q"
            type="text"
            placeholder="Angebot oder Thema…"
          >
        </div>

        <div
          class="field filters__field filters__field--location"
        >
          <label for="f-location">Ort</label>
          <select
            id="f-location"
            v-model="locationFilter"
          >
            <option value="">
              Alle
            </option>
            <option
              v-for="loc in uniqueLocations"
              :key="loc"
              :value="loc"
            >
              {{ loc }}
            </option>
          </select>
        </div>

        <div
          class="field filters__field filters__field--teacher"
        >
          <label for="f-teacher">Verantwortlich</label>
          <select
            id="f-teacher"
            v-model="teacherFilter"
          >
            <option value="">
              Alle
            </option>
            <option
              v-for="t in uniqueTeachers"
              :key="t"
              :value="t"
            >
              {{ t }}
            </option>
          </select>
        </div>

        <div
          class="field filters__field filters__field--mandatory"
        >
          <label for="f-mandatory">Pflicht</label>
          <select
            id="f-mandatory"
            v-model="mandatoryFilter"
          >
            <option value="">
              Alle
            </option>
            <option value="yes">
              Ja
            </option>
            <option value="no">
              Nein
            </option>
          </select>
        </div>

        <div
          class="field filters__field filters__field--status"
        >
          <label for="f-status">Status</label>
          <select
            id="f-status"
            v-model="statusFilter"
          >
            <option value="bereit, gestartet, in Vorbereitung">
              aktiv
            </option>
            <option value="">
              alle
            </option>
            <option value="bereit">
              bereit
            </option>
            <option value="gestartet">
              gestartet
            </option>
            <option value="in Vorbereitung">
              in Vorbereitung
            </option>
            <option value="abgeschlossen">
              abgeschlossen
            </option>
            <option value="abgesagt">
              abgesagt
            </option>
          </select>
        </div>

        <div class="field text-center filters__field filters__field--reset">
          <button
            id="f-reset"
            class="btn btn-primary d-inline"
            :disabled="!hasActiveFilters"
            @click="resetFilters"
          >
            Reset
          </button>
        </div>
      </div>

      <!-- Tabelle (Desktop ≥1200px) -->
      <div class="table offer-table">
        <div
          class="table-header"
        >
          <div
            class="sortable"
            role="button"
            tabindex="0"
            @click="setSort('time')"
            @keydown.enter="setSort('time')"
          >
            Zeit <span v-if="sortKey === 'time'">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
          </div>
          <div>Angebot / Fach</div>
          <div>Thema</div>
          <div
            class="sortable"
            role="button"
            tabindex="0"
            @click="setSort('location')"
            @keydown.enter="setSort('location')"
          >
            Ort <span v-if="sortKey === 'location'">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
          </div>
          <div
            class="sortable"
            role="button"
            tabindex="0"
            @click="setSort('teacher')"
            @keydown.enter="setSort('teacher')"
          >
            Verantwortlich <span v-if="sortKey === 'teacher'">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
          </div>
          <div>Pflicht</div>
          <div class="text-center">
            Status
          </div>
          <div class="text-center">
            Schüler
          </div>
          <div class="text-right">
            Aktionen
          </div>
        </div>

        <div
          v-for="e in paged"
          :key="e.id"
          class="table-row"
          :class="{ 'row-conflict': conflictIds.has(e.id) }"
          :title="conflictIds.has(e.id) ? 'Zeitüberschneidung mit anderem Angebot dieser Lehrkraft' : null"
        >
          <!-- Zeit -->
          <div class="muted">
            <div
              v-if="isMultiDay(e)"
              class="small"
            >
              {{ fmtDateRange(e) }}
            </div>
            <div>{{ fmtTime(e.starts_at) }} – {{ fmtTime(e.ends_at) }}</div>
          </div>

          <!-- Angebot -->
          <div>
            <button
              type="button"
              class="btn-inline-edit"
              :aria-label="`Angebotsnamen bearbeiten: ${getOfferTitle(e)}`"
              @click="openInlineEdit(e, 'title')"
            >
              <span :class="offerTitleClass(e)">{{ getOfferTitle(e) }}</span>
            </button>
            <i
              v-if="e.generated === 'auto'"
              class="pi pi-sync offer-auto-icon ml-2"
              aria-hidden="true"
              :title="'Automatisch generiert'"
              style="vertical-align: baseline; color: forestgreen; font-weight: 900;"
            />
          </div>

          <!-- Thema -->
          <div>
            <button
              type="button"
              class="btn-inline-edit btn-inline-edit--muted"
              :aria-label="`Thema bearbeiten: ${e.topic || 'kein Thema'}`"
              @click="openInlineEdit(e, 'topic')"
            >
              <span class="muted text-left">{{ e.topic || '—' }}</span>
            </button>
          </div>

          <!-- Ort -->
          <div class="muted">
            {{ e.location || '—' }}
          </div>

          <!-- Lehrer -->
          <div class="muted">
            {{ fmtTeacherShort(e) }}
          </div>

          <!-- Pflicht -->
          <div>
            <span :class="['pill', isMandatory(e) ? 'pill-mandatory' : 'pill-optional']">
              {{ isMandatory(e) ? 'Ja' : 'Nein' }}
            </span>
          </div>

          <!-- Status (UI: aktiv / in Vorbereitung / abgesagt) -->
          <div class="text-center muted">
            {{ mapStatus(e.status) }}
          </div>

          <!-- Schüler -->
          <div class="text-center">
            <button
              v-if="(e.participantsCount || 0) > 0 && ['ready','started','completed'].includes(e.status)"
              class="icon-btn attendance-btn"
              :title="'Anwesenheiten'"
              :aria-label="'Anwesenheiten'"
              @click="openAttendance(e)"
            >
              {{ e.participantsCount }}
            </button>
            <span
              v-else
              class="muted"
            >{{ e.participantsCount ?? '—' }}</span>
          </div>

          <!-- Aktionen -->
          <div class="row-actions">
            <button
              v-for="a in getRowActions(e)"
              :key="a.key"
              class="icon-btn"
              :class="a.variant || ''"
              :title="isActionDisabledForEvent(e) ? 'Aktionen für anderes Schuljahr gesperrt' : a.title"
              :aria-label="isActionDisabledForEvent(e) ? 'Aktionen für anderes Schuljahr gesperrt' : a.title"
              :disabled="isActionDisabledForEvent(e)"
              @click="onRowAction(a.key, e)"
            >
              <i
                :class="['pi', a.icon]"
                aria-hidden="true"
              />
            </button>
          </div>
        </div>

        <p
          v-if="!loading && filtered.length === 0"
          class="muted"
        >
          Keine Einträge.
        </p>
        <p
          v-if="error"
          class="notice notice-danger mt-12"
          role="alert"
        >
          {{ error }}
        </p>
      </div>

      <!-- Card-Ansicht (Tablet/Mobile ≤1199px) -->
      <div
        v-if="!loading"
        class="offer-cards mt-12"
      >
        <article
          v-for="e in paged"
          :key="`offer-card-${e.id}`"
          class="offer-card"
          :class="{ 'row-conflict': conflictIds.has(e.id) }"
          :title="conflictIds.has(e.id) ? 'Zeitüberschneidung mit anderem Angebot dieser Lehrkraft' : null"
        >
          <header class="offer-card__header">
            <div class="offer-card__time">
              <div
                v-if="isMultiDay(e)"
                class="small muted"
              >
                {{ fmtDateRange(e) }}
              </div>
              <div class="offer-card__time-row font-medium">
                <span>{{ fmtTime(e.starts_at) }} – {{ fmtTime(e.ends_at) }}</span>
                <span class="font-light"> ({{ fmtTeacherShort(e) }})</span>
              </div>
            </div>
            <div class="offer-card__status">
              <span
                :class="['offer-card__status-pill', ['ready','started'].includes(e.status) ? 'bg-green-100' : '']"
              >
                {{ mapStatus(e.status) }}
              </span>
            </div>
          </header>

          <div class="offer-card__body">
            <div class="offer-card__title-row">
              <button
                type="button"
                class="btn-inline-edit"
                :aria-label="`Angebotsnamen bearbeiten: ${getOfferTitle(e)}`"
                @click="openInlineEdit(e, 'title')"
              >
                <span :class="['offer-card__title', offerTitleClass(e)]">{{ getOfferTitle(e) }}</span>
              </button>
              <i
                v-if="e.generated === 'auto'"
                class="pi pi-sync offer-auto-icon"
                aria-hidden="true"
                :title="'Automatisch generiert'"
              />
            </div>

            <p class="offer-card__topic">
              <button
                type="button"
                class="btn-inline-edit btn-inline-edit--muted"
                :aria-label="`Thema bearbeiten: ${e.topic || 'kein Thema'}`"
                @click="openInlineEdit(e, 'topic')"
              >
                <span class="muted">{{ e.topic || 'Kein Thema' }}</span>
              </button>
            </p>

            <dl class="offer-card__meta">
              <div class="offer-card__meta-row">
                <span class="muted">{{ e.location }}</span>
                <span> — </span>
                <span class="font-medium">
                  <button
                    v-if="(e.participantsCount || 0) > 0 && ['ready','started','completed'].includes(e.status)"
                    class="icon-btn attendance-btn"
                    :title="'Anwesenheiten'"
                    :aria-label="'Anwesenheiten'"
                    @click="openAttendance(e)"
                  >
                    {{ e.participantsCount }} Schüler
                  </button>
                  <span
                    v-else
                  >{{ e.participantsCount ?? '—' }} Schüler</span>
                </span>
              </div>
            </dl>
            <span
              :class="['offer-card__status-pill', isMandatory(e) ? 'pill-mandatory' : 'pill-optional']"
            >{{ isMandatory(e) ? 'Pflichtangebot' : 'freiwillig' }}
            </span>
          </div>

          <footer class="offer-card__footer">
            <div class="offer-card__actions">
              <button
                v-for="a in getRowActions(e)"
                :key="a.key"
                class="icon-btn"
                :class="a.variant || ''"
                :title="isActionDisabledForEvent(e) ? 'Aktionen für anderes Schuljahr gesperrt' : a.title"
                :aria-label="isActionDisabledForEvent(e) ? 'Aktionen für anderes Schuljahr gesperrt' : a.title"
                :disabled="isActionDisabledForEvent(e)"
                @click="onRowAction(a.key, e)"
              >
                <i
                  :class="['pi', a.icon]"
                  aria-hidden="true"
                />
              </button>
            </div>
          </footer>
        </article>

        <p
          v-if="!loading && filtered.length === 0"
          class="muted"
        >
          Keine Einträge.
        </p>
        <p
          v-if="error"
          class="notice notice-danger mt-12"
          role="alert"
        >
          {{ error }}
        </p>
      </div>

      <!-- Pagination -->
      <div
        class="mt-12 pagination-row"
        style="display:flex;align-items:center;gap:8px;justify-content:flex-end;"
      >
        <span
          class="muted mobile-hide"
          style="margin-right:auto"
        >Gesamt: {{ realTotal }}</span>
        <label
          class="muted mobile-hide"
          for="of-limit"
        >pro Seite:</label>
        <select
          id="of-limit"
          class="mobile-hide"
          :value="limit"
          @change="changeLimit"
        >
          <option
            v-for="n in [10,20,50,100]"
            :key="n"
            :value="n"
          >
            {{ n }}
          </option>
        </select>
        <button
          class="btn"
          :disabled="page<=1"
          @click="prevPage"
        >
          Zurück
        </button>
        <span class="muted">Seite {{ page }} / {{ pages }}</span>
        <button
          class="btn"
          :disabled="page>=pages"
          @click="nextPage"
        >
          Weiter
        </button>
      </div>
    </section>

    <!-- Dialoge -->
    <BaseDialog
      :open="!!toDelete"
      :disabled="loading"
      @close="toDelete=null"
    >
      <template #title>
        Angebot löschen?
      </template>
      <p>Diese Aktion kann nicht rückgängig gemacht werden.</p>
      <div
        class="mt-12"
        style="display:flex;gap:8px;justify-content:flex-end;"
      >
        <button
          class="btn"
          :disabled="loading"
          @click="toDelete=null"
        >
          Abbrechen
        </button>
        <button
          class="btn btn-warn"
          :disabled="loading"
          @click="doDelete"
        >
          <span
            v-if="loading"
            class="spinner"
            aria-hidden="true"
          /> Löschen
        </button>
      </div>
    </BaseDialog>

    <EventCompleteDialog
      :open="!!toComplete"
      :event-id="toComplete ? toComplete.id : null"
      :event-title="toComplete ? getOfferTitle(toComplete) : null"
      :event-mandatory="toComplete ? isMandatory(toComplete) : false"
      :event-starts-at="toComplete ? (toComplete.starts_at || toComplete.startsAt) : null"
      :event-ends-at="toComplete ? (toComplete.ends_at || toComplete.endsAt) : null"
      @close="toComplete=null"
      @completed="onCompletedFromCompleteDialog"
    />

    <EditEventDialog
      v-model="editOpen"
      :event="editEvent"
      @saved="onEdited"
    />
    <DuplicateEventDialog
      v-model="duplicateOpen"
      :event="duplicateEvent"
      @saved="onDuplicated"
    />
    <CancelEventDialog
      :open="cancelOpen"
      :event-id="cancelEvent ? cancelEvent.id : null"
      @close="cancelOpen=false"
      @canceled="onCanceled"
    />
    <CreateEventDialog
      v-model="openCreate"
      @saved="onCreated"
    />

    <OfferAssignDialog
      :open="assignOpen"
      :offer="assignEvent ? { id: assignEvent.id, title: getOfferTitle(assignEvent), topic: assignEvent.topic || null, teacher: fmtTeacherShort(assignEvent) } : null"
      @close="assignOpen=false"
      @applied="onAssigned"
    />

    <EventAttendanceDialog
      :open="attendanceOpen"
      :event-id="attendanceEvent ? attendanceEvent.id : null"
      :event-status="attendanceEvent ? attendanceEvent.status : null"
      :event-title="attendanceEvent ? getOfferTitle(attendanceEvent) : null"
      :event-mandatory="attendanceEvent ? isMandatory(attendanceEvent) : false"
      :event-starts-at="attendanceEvent ? (attendanceEvent.starts_at || attendanceEvent.startsAt) : null"
      :event-ends-at="attendanceEvent ? (attendanceEvent.ends_at || attendanceEvent.endsAt) : null"
      @close="attendanceOpen=false"
      @updated="onAttendanceUpdated"
    />

    <!-- Inline-Dialog zum Bearbeiten von Angebotsname/Thema -->
    <BaseDialog
      :open="inlineEditOpen"
      :disabled="inlineEditSaving"
      role="dialog"
      aria-modal="true"
      @close="onInlineEditClose"
    >
      <template #title>
        {{ inlineEditField === 'title' ? 'Angebotsnamen bearbeiten' : 'Thema bearbeiten' }}
      </template>

      <div class="field">
        <label
          :for="inlineEditField === 'title' ? 'inline-edit-title' : 'inline-edit-topic'"
        >
          {{ inlineEditField === 'title' ? 'Angebot / Fach' : 'Thema' }}
        </label>
        <input
          :id="inlineEditField === 'title' ? 'inline-edit-title' : 'inline-edit-topic'"
          v-model="inlineEditValue"
          type="text"
          @keyup.enter.prevent="onInlineEditSave"
        >
        <p
          v-if="inlineEditError"
          class="help danger"
          role="alert"
        >
          {{ inlineEditError }}
        </p>
      </div>

      <div
        class="mt-12"
        style="display:flex;gap:8px;justify-content:flex-end;"
      >
        <button
          class="btn"
          type="button"
          :disabled="inlineEditSaving"
          @click="onInlineEditClose"
        >
          Abbrechen
        </button>
        <button
          class="btn btn-primary"
          type="button"
          :disabled="inlineEditSaving"
          @click="onInlineEditSave"
        >
          <span
            v-if="inlineEditSaving"
            class="spinner"
            aria-hidden="true"
          />
          Speichern
        </button>
      </div>
    </BaseDialog>

    <teleport to=".top-nav-help-slot">
      <span
        id="top-nav-help-offers"
        class="top-nav-help"
      >
        <UiInfoButton title="Hilfe: Angebote">
          <ul>
            <li>Filter: Datum (heute vorausgewählt), Suche (Angebot/Thema), Ort, Verantwortlich, Pflicht und Status.</li>
            <li>Sortieren: Spalten „Zeit“, „Ort“ und „Verantwortlich“ sind klickbar (▲/▼ zeigt die Richtung).</li>
            <li>Aktionen je Status (geplant/bereit/gestartet/abgesagt/abgeschlossen) sind rechts in jeder Zeile verfügbar.</li>
            <li>Konflikte gleicher Lehrkraft werden in der Liste rot hinterlegt; Hinweis via Tooltip im Titel.</li>
            <li>Teilnehmerzahl-Button öffnet die Anwesenheiten (startet ggf. automatisch aus „bereit“).</li>
            <li>Paginierung unten: Gesamtanzahl, „pro Seite“, sowie Navigation zwischen den Seiten.</li>
            <li>„Neu“-Button im Kopfbereich legt ein neues Angebot an.</li>
          </ul>
        </UiInfoButton>
      </span>
    </teleport>
  </main>
</template>

<script setup>
import {ref, computed, onMounted, watch} from 'vue';
import {eventsApi} from '@/services/api/events';
import api from '@/services/api';
import CancelEventDialog from '@/components/events/CancelEventDialog.vue';
import CreateEventDialog from '@/components/events/CreateEventDialog.vue';
import EditEventDialog from '@/components/events/EditEventDialog.vue';
import DuplicateEventDialog from '@/components/events/DuplicateEventDialog.vue';
import BaseDialog from '@/components/BaseDialog.vue';
import UiInfoButton from '@/components/UiInfoButton.vue';
import OfferAssignDialog from '@/components/OfferAssignDialog.vue';
import EventAttendanceDialog from '@/components/events/EventAttendanceDialog.vue';
import EventCompleteDialog from '@/components/events/EventCompleteDialog.vue';
import {useToast} from 'primevue/usetoast';
import {getCurrentSchoolYear} from '@/api/schoolYears';

const emit = defineEmits(['start', 'assign', 'edit', 'duplicate', 'delete', 'cancel']);
import FlatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import {German} from 'flatpickr/dist/l10n/de.js';

const fpOpts = {
  altInput: true,
  altFormat: 'd.m.Y',
  dateFormat: 'Y-m-d',
  locale: German,
  disableMobile: true
};

const date = ref(new Date().toISOString().slice(0, 10));
const q = ref('');
const events = ref([]);
const loading = ref(false);
const error = ref(null);
const openCreate = ref(false);
const toast = useToast();
// Filter-States
const locationFilter = ref('');
const teacherFilter = ref('');
const mandatoryFilter = ref(''); // '', 'yes', 'no'
const statusFilter = ref('bereit, gestartet, in Vorbereitung');
// Sortierung (initial: Zeit asc)
const sortKey = ref('time'); // 'time' | 'location' | 'teacher'
const sortDir = ref('asc');

const cancelOpen = ref(false);
const cancelEvent = ref(null);
const toDelete = ref(null);
const toComplete = ref(null);
const editOpen = ref(false);
const editEvent = ref(null);
const duplicateOpen = ref(false);
const duplicateEvent = ref(null);
const assignOpen = ref(false);
const assignEvent = ref(null);
const attendanceOpen = ref(false);
const attendanceEvent = ref(null);

// Inline-Editing: Angebotsname / Thema
const inlineEditOpen = ref(false);
const inlineEditEvent = ref(null);
const inlineEditField = ref('title'); // 'title' | 'topic'
const inlineEditValue = ref('');
const inlineEditSaving = ref(false);
const inlineEditError = ref('');

// Aktuelles Schuljahr und Hilfsfunktionen
const currentSchoolYear = ref(null);

function toDateString(val) {
  if (!val) return null;
  if (typeof val === 'string') {
    // ISO- oder 'YYYY-MM-DD' → erste 10 Zeichen genügen für lexikographischen Vergleich
    if (val.length >= 10) return val.slice(0, 10);
  }
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
}

function pickDateStr(obj, keys) {
  for (const k of keys) {
    const v = obj?.[k];
    const s = toDateString(v);
    if (s) return s;
  }
  return null;
}

function isActionDisabledForEvent(e) {
  const ed = toDateString(e?.ends_at || e?.endsAt);
  const sy = currentSchoolYear.value;
  if (!sy) return false; // Ohne Schuljahr keine Sperre anwenden
  const syStart = pickDateStr(sy, ['start_date', 'startDate', 'starts_at', 'startsAt', 'start']);
  const syEnd = pickDateStr(sy, ['end_date', 'endDate', 'ends_at', 'endsAt', 'end']);
  if (!ed || !syStart) return false; // unklare Daten → nicht sperren
  if (!syEnd) {
    // Offenes Schuljahr nach oben: nur frühere Daten sperren
    return ed < syStart;
  }
  // Außerhalb [Start, Ende] → sperren
  return ed < syStart || ed > syEnd;
}

async function loadCurrentSchoolYear() {
  try {
    currentSchoolYear.value = await getCurrentSchoolYear();
  } catch (err) {
    // Bei Fehler weiterarbeiten, ohne zu sperren
    console.warn('Schuljahr laden fehlgeschlagen.', err);
  }
}

function fmtTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

// Mehrtägig? (Vergleich auf lokales Datum)
function isMultiDay(e) {
  const sa = e?.starts_at || e?.startsAt;
  const ea = e?.ends_at || e?.endsAt;
  if (!sa || !ea) return false;
  const s = new Date(sa);
  const t = new Date(ea);
  return s.getFullYear() !== t.getFullYear()
      || s.getMonth() !== t.getMonth()
      || s.getDate() !== t.getDate();
}

function fmtDateRange(e) {
  const sa = e?.starts_at || e?.startsAt;
  const ea = e?.ends_at || e?.endsAt;
  if (!sa || !ea) return '';
  const s = new Date(sa);
  const t = new Date(ea);
  const dd = (n) => String(n).padStart(2, '0');
  const start = `${dd(s.getDate())}.${dd(s.getMonth() + 1)}.`;
  const end = `${dd(t.getDate())}.${dd(t.getMonth() + 1)}.`;
  return `${start} - ${end}`;
}

// Hilfsfunktionen für Konflikt-Zeitdarstellung (wie im Zuweisungsdialog)

// UI-Status-Mapping
function mapStatus(s) {
  if (s === 'ready') return 'bereit';
  if (s === 'planned') return 'in Vorbereitung';
  if (s === 'started') return 'gestartet';
  if (s === 'canceled') return 'abgesagt';
  if (s === 'completed') return 'abgeschlossen';
  return s || '—';
}

function offerTitleClass(e) {
  const s = String(e?.status || '').toLowerCase();
  if (s === 'canceled') return 'offer-title offer-title--canceled';
  if (s === 'completed') return 'offer-title offer-title--completed';
  return 'offer-title';
}


// Dispatch: wo schon vorhanden, nutzen wir bestehende Funktionen – Rest nur emitten
function onRowAction(key, e) {
  switch (key) {
    case 'ready':
      return readyEvent(e);
    case 'start':
      return startEvent(e);
    case 'cancel':
      return openCancel(e);
    case 'edit':
      return openEdit(e);
    case 'duplicate':
      return openDuplicate(e);
    case 'delete':
      return confirmDelete(e);
    case 'assign':
      return openAssign(e);
    case 'complete':
      return openComplete(e);
    default:
      return emit(key, e);
  }
}

// Helfer: Angebotstitel
function getOfferTitle(e) {
  // Fallbacks: subject (falls geliefert) oder topic anzeigen, wenn keine Titel-Felder vorhanden sind
  return e?.templateTitle || e?.offerTitle || e?.title || e?.subject || e?.topic || '—';
}

// Helfer: Lehrer anzeigen (H. Müller)
function fmtTeacherShort(e) {
  const t = e?.teacher || {};
  const fn = t.firstName || e?.teacherFirstName || e?.teacher_first_name || '';
  const ln = t.lastName || e?.teacherLastName || e?.teacher_last_name || e?.teacherName || '';
  if (!fn && !ln) {
    // Fallback: teacher_id anzeigen, wenn keine Namensfelder geliefert wurden
    return e?.teacher_id ? String(e.teacher_id) : '—';
  }
  const first = String(fn).trim() || '';
  const initial = ln ? `${String(ln).trim()[0]}.` : '';
  return `${first} ${initial}`.trim();
}

// Eindeutiger Schlüssel zur Identifikation derselben Lehrkraft: ausschließlich user_id-basiert
function getTeacherKey(e) {
  // Bevorzugt: e.user_id (explizit gefordert)
  if (e && e.user_id != null) return e.user_id;
  // Fallbacks, falls Backend anders liefert (nutzen weiterhin IDs, keine Namen)
  if (e?.teacher?.id != null) return e.teacher.id;
  if (e?.teacher_id != null) return e.teacher_id;

  return null; // ohne ID keine Gruppierung ⇒ keine Konfliktmarkierung
}

// Hilfsfunktionen für Zeitvergleich
function getStart(e) {
  return new Date(e?.starts_at || e?.startsAt || 0);
}

function getEnd(e) {
  return new Date(e?.ends_at || e?.endsAt || 0);
}

function overlaps(a, b) {
  const as = getStart(a), ae = getEnd(a);
  const bs = getStart(b), be = getEnd(b);
  if (isNaN(+as) || isNaN(+ae) || isNaN(+bs) || isNaN(+be)) return false;
  // Überlappung, wenn Start < anderes Ende und Ende > anderer Start
  return as < be && ae > bs;
}

// Ermitteln der Konflikt-IDs (zeitliche Überschneidungen je user_id)
const conflictIds = computed(() => {
  const set = new Set();
  // Nur relevante Status für die Überschneidungsprüfung berücksichtigen
  const allowed = new Set(['planned', 'ready', 'started']);
  const list = (events.value || []).filter(ev => allowed.has(ev?.status));
  // nach Lehrkraft (user_id) gruppieren
  const byTeacher = new Map();
  for (const ev of list) {
    const k = getTeacherKey(ev);
    if (k == null) continue; // ohne ID keine Prüfung
    if (!byTeacher.has(k)) byTeacher.set(k, []);
    byTeacher.get(k).push(ev);
  }
  // je Gruppe sortieren und Überlappung prüfen
  for (const [, arr] of byTeacher.entries()) {
    if (arr.length < 2) continue;
    const sorted = [...arr].sort((a, b) =>
        String(a.starts_at || a.startsAt || '').localeCompare(String(b.starts_at || b.startsAt || ''))
    );
    for (let i = 0; i < sorted.length; i++) {
      for (let j = i + 1; j < sorted.length; j++) {
        // Optionaler Early-Break: wenn der nächste Start >= aktuelles Ende, kann abgebrochen werden
        const aiEnd = getEnd(sorted[i]);
        const bjStart = getStart(sorted[j]);
        if (!isNaN(+aiEnd) && !isNaN(+bjStart) && bjStart >= aiEnd) break;
        if (overlaps(sorted[i], sorted[j])) {
          if (sorted[i]?.id != null) set.add(sorted[i].id);
          if (sorted[j]?.id != null) set.add(sorted[j].id);
        }
      }
    }
  }
  return set;
});


// Helfer: Pflicht ja/nein
function isMandatory(e) {
  return Boolean(e?.mandatory ?? e?.isMandatory ?? e?.required ?? false);
}

// Optionen für Filter
const uniqueLocations = computed(() =>
    Array.from(new Set((events.value || []).map(e => e.location).filter(Boolean))).sort((a, b) =>
        String(a).localeCompare(String(b), 'de', {sensitivity: 'base'})
    )
);
const uniqueTeachers = computed(() =>
    Array.from(new Set((events.value || []).map(e => fmtTeacherShort(e)).filter(v => v && v !== '—'))).sort((a, b) =>
        String(a).localeCompare(String(b), 'de', {sensitivity: 'base'})
    )
);

// Reset-Button aktivieren, wenn irgendein Filter gesetzt ist (Datum zählt nicht)
const hasActiveFilters = computed(() =>
    Boolean(q.value.trim() || locationFilter.value || teacherFilter.value || mandatoryFilter.value || statusFilter.value)
);

// Filter-Pipeline
const filtered = computed(() => {
  const term = q.value.toLowerCase().trim();
  let base = events.value || [];

  // Ort
  if (locationFilter.value) {
    base = base.filter(e => (e.location || '') === locationFilter.value);
  }
  // Lehrer
  if (teacherFilter.value) {
    base = base.filter(e => fmtTeacherShort(e) === teacherFilter.value);
  }
  // Pflicht
  if (mandatoryFilter.value) {
    base = base.filter(e => (mandatoryFilter.value === 'yes' ? isMandatory(e) : !isMandatory(e)));
  }
  // Status (UI → Filter)
  if (statusFilter.value) {
    // Kombi-Wert: "aktiv, in Vorbereitung"
    const parseWanted = (val) => {
      const map = {
        'bereit': 'ready',
        'gestartet': 'started',
        'in Vorbereitung': 'planned',
        'abgeschlossen': 'completed',
        'abgesagt': 'canceled'
      };
      return new Set(
          String(val)
              .split(',')
              .map(s => s.trim())
              .map(s => map[s])
              .filter(Boolean)
      );
    };
    const want = parseWanted(statusFilter.value);
    if (want.size) {
      base = base.filter(e => want.has(e.status));
    }
  }

  // Suche in Angebot + Thema
  if (term) {
    base = base.filter(e => {
      const fields = [getOfferTitle(e), e.topic || ''].map(s => String(s).toLowerCase());
      return fields.some(f => f.includes(term));
    });
  }
  return base;
});

// Sortierung anwenden
const sorted = computed(() => {
  const arr = [...filtered.value];
  arr.sort((a, b) => {
    let va, vb;
    switch (sortKey.value) {
      case 'location':
        va = a.location || '';
        vb = b.location || '';
        break;
      case 'teacher':
        va = fmtTeacherShort(a);
        vb = fmtTeacherShort(b);
        break;
      case 'time':
      default:
        va = a.starts_at || a.startsAt || '';
        vb = b.starts_at || b.startsAt || '';
        break;
    }
    let result;
    if (sortKey.value === 'time') {
      // ISO-Strings vergleichen (stabil)
      result = String(va).localeCompare(String(vb));
    } else {
      result = String(va).localeCompare(String(vb), 'de', {sensitivity: 'base'});
    }
    return sortDir.value === 'asc' ? result : -result;
  });
  return arr;
});

function setSort(key) {
  if (sortKey.value === key) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortKey.value = key;
    sortDir.value = 'asc';
  }
}

// Pagination (clientseitig, analog zu Groups)
const page = ref(1);
const limit = ref(20);
const total = computed(() => filtered.value.length);
// "Reale" Gesamtmenge (unabhängig von sichtbaren Zeilen/Seite)
const realTotal = computed(() => (events.value || []).length);

const pages = computed(() => Math.max(1, Math.ceil(total.value / limit.value)));
const paged = computed(() => {
  const start = (page.value - 1) * limit.value;
  return sorted.value.slice(start, start + limit.value);
});

function nextPage() {
  if (page.value < pages.value) {
    page.value++;
  }
}

function prevPage() {
  if (page.value > 1) {
    page.value--;
  }
}

function changeLimit(e) {
  limit.value = Number(e.target.value);
  page.value = 1;
}

// Seite clampen, wenn sich Gesamtmenge/Limits ändern
watch([total, limit], () => {
  if (page.value > pages.value) page.value = pages.value;
  if (page.value < 1) page.value = 1;
});

async function load() {
  loading.value = true;
  error.value = null;
  try {
    const res = await eventsApi.getToday({date: date.value, teacherId: 'all'});
    events.value = res?.data || [];
    page.value = 1;
  } catch (e) {
    error.value = 'Laden fehlgeschlagen.';
  } finally {
    loading.value = false;
  }
}

// Icons + Ausgabe-Logik für Zeilenaktionen (nur Anzeige; Funktionen folgen)
const ACTION_ICONS = {
  start: 'pi-check',
  assign: 'pi-user',
  edit: 'pi-pencil',
  duplicate: 'pi-copy',
  complete: 'pi-check-circle',
  delete: 'pi-trash',
  cancel: 'pi-times'
};

function getRowActions(row) {
  const status = typeof row === 'string' ? row : (row?.status ?? '');
  const hasStudents = typeof row === 'object'
      ? Number(row?.participantsCount ?? row?.participants_count ?? 0) > 0
      : true; // Fallback: wenn nur Status übergeben wurde

  switch (status) {
    case 'planned': {
      const base = [
        {key: 'assign', title: 'Zuweisen', icon: ACTION_ICONS.assign},
        {key: 'edit', title: 'Bearbeiten', icon: ACTION_ICONS.edit},
        {key: 'duplicate', title: 'Duplizieren', icon: ACTION_ICONS.duplicate},
        {key: 'delete', title: 'Löschen', icon: ACTION_ICONS.delete, variant: 'danger'},
      ];
      return hasStudents
          ? [{key: 'ready', title: 'Bereitstellen', icon: ACTION_ICONS.start}, ...base]
          : base;
    }
    case 'started':
      return [
        {key: 'edit', title: 'Bearbeiten', icon: ACTION_ICONS.edit},
        {key: 'duplicate', title: 'Duplizieren', icon: ACTION_ICONS.duplicate},
        {key: 'complete', title: 'Abschließen', icon: ACTION_ICONS.complete, variant: 'success'},
      ];
    case 'ready':
      return [
        {key: 'edit', title: 'Bearbeiten', icon: ACTION_ICONS.edit},
        {key: 'duplicate', title: 'Duplizieren', icon: ACTION_ICONS.duplicate},
        {key: 'cancel', title: 'Absagen', icon: ACTION_ICONS.cancel, variant: 'danger'},
      ];
    case 'canceled':
      return [
        {key: 'edit', title: 'Bearbeiten', icon: ACTION_ICONS.edit},
        {key: 'duplicate', title: 'Duplizieren', icon: ACTION_ICONS.duplicate},
        {key: 'delete', title: 'Löschen', icon: ACTION_ICONS.delete, variant: 'danger'},
      ];
    case 'completed':
      return [
        {key: 'edit', title: 'Bearbeiten', icon: ACTION_ICONS.edit},
        {key: 'duplicate', title: 'Duplizieren', icon: ACTION_ICONS.duplicate},
      ];
    default:
      return [];
  }
}

function resetFilters() {
  date.value = new Date().toISOString().slice(0, 10);
  q.value = '';
  locationFilter.value = '';
  teacherFilter.value = '';
  mandatoryFilter.value = '';
  statusFilter.value = 'bereit, gestartet, in Vorbereitung';
  load();
}

function openCancel(e) {
  cancelEvent.value = e;
  cancelOpen.value = true;
}

function openEdit(e) {
  editEvent.value = e;
  editOpen.value = true;
}

function onEdited() {
  editOpen.value = false;
  // Einfachheit halber: Liste neu laden (stellt auch Sortierung/Filter konsistent dar)
  load();
}

function openDuplicate(e) {
  duplicateEvent.value = e;
  duplicateOpen.value = true;
}

function onDuplicated() {
  duplicateOpen.value = false;
  load();
}

function openComplete(e) {
  toComplete.value = e;
}

function openAssign(e) {
  assignEvent.value = e;
  assignOpen.value = true;
}

function onAssigned() {
  // Dialog offen lassen; nur Liste aktualisieren, damit Teilnehmerzahl sich aktualisiert
  load();
}

function openAttendance(e) {
  attendanceEvent.value = e;
  (async () => {
    try {
      // Nur einmal starten, wenn der Status noch 'ready' ist
      if (e?.status === 'ready') {
        await api.post(`/events/${e.id}/start`);
        // Lokalen Status aktualisieren, damit es nicht erneut ausgeführt wird
        e.status = 'started';
        // Liste aktualisieren, um Zähler/Status zu synchronisieren
        await load();
        // attendanceEvent nach Reload neu setzen (damit die Props korrekt sind)
        const ne = (events.value || []).find(ev => ev.id === e.id);
        if (ne) attendanceEvent.value = ne;
      }
    } catch (err) {
      // Fallback: Fehlermeldung setzen, Dialog dennoch öffnen
      error.value = 'Starten fehlgeschlagen.';
    } finally {
      attendanceOpen.value = true;
    }
  })();
}

function onAttendanceUpdated() {
  // Liste aktualisieren (z. B. Teilnehmerzahl)
  load();
}

function onCompletedFromCompleteDialog() {
  toComplete.value = null;
  toast.add({severity: 'success', summary: 'Angebot abgeschlossen.', life: 3500});
  load();
}

function confirmDelete(e) {
  toDelete.value = e;
}

async function doDelete() {
  if (!toDelete.value) return;
  loading.value = true;
  error.value = null;
  try {
    await eventsApi.deleteEvent(toDelete.value.id);
    events.value = (events.value || []).filter(ev => ev.id !== toDelete.value.id);
    toDelete.value = null;
    toast.add({severity: 'success', summary: 'Gelöscht.', life: 3500});
    // Seite neu clampen via Watcher (total ändert sich)
  } catch (e) {
    error.value = 'Löschen fehlgeschlagen.';
  } finally {
    loading.value = false;
  }
}

function shiftDay(delta) {
  if (!date.value) {
    date.value = new Date().toISOString().slice(0, 10);
  }
  const d = new Date(date.value);
  if (Number.isNaN(d.getTime())) {
    date.value = new Date().toISOString().slice(0, 10);
  } else {
    d.setDate(d.getDate() + delta);
    date.value = d.toISOString().slice(0, 10);
  }
}

function prevDay() {
  shiftDay(-1);
}

function nextDay() {
  shiftDay(1);
}

async function readyEvent(e) {
  if (!e?.id) return;
  loading.value = true;
  error.value = null;

  try {
    // Parallele Angebote mit gleichen Schülern sind erlaubt – direkte Bereitstellung.
    await api.post(`/events/${e.id}/ready`);
    await load();
  } catch (err) {
    error.value = 'Bereitstellen fehlgeschlagen.';
  } finally {
    loading.value = false;
  }
}

async function startEvent(e) {
  await api.post(`/events/${e.id}/start`);
  await load();
}

function onCanceled() {
  cancelOpen.value = false;
  toast.add({severity: 'success', summary: 'Angebot abgesagt.', life: 3500});
  load();
}

function onCreated() {
  // Dialog schließt sich selbst; Liste aktualisieren
  load();
}

// Inline-Editing öffnen
function openInlineEdit(event, field) {
  inlineEditEvent.value = event;
  inlineEditField.value = field; // 'title' | 'topic'
  inlineEditError.value = '';
  inlineEditValue.value =
      field === 'title'
          ? getOfferTitle(event)
          : (event.topic || '');
  inlineEditOpen.value = true;
}

// Inline-Editing schließen
function onInlineEditClose() {
  if (inlineEditSaving.value) {
    return;
  }
  inlineEditOpen.value = false;
  inlineEditEvent.value = null;
  inlineEditValue.value = '';
  inlineEditError.value = '';
}

// Inline-Editing speichern (PATCH auf Event)
async function onInlineEditSave() {
  const raw = inlineEditValue.value;
  const trimmed = raw.trim();

  if (!trimmed) {
    inlineEditError.value = 'Der Text darf nicht leer sein.';
    return;
  }

  const event = inlineEditEvent.value;
  if (!event?.id) {
    inlineEditError.value = 'Ungültiges Angebot.';
    return;
  }

  inlineEditSaving.value = true;
  inlineEditError.value = '';

  try {
    // Wichtig: Angebotsname → subject, Thema → topic
    const payload = inlineEditField.value === 'title'
        ? {subject: trimmed}
        : {topic: trimmed};

    await api.patch(`/events/${event.id}`, payload);
    await load(); // Liste aktualisieren
    inlineEditOpen.value = false;
    inlineEditEvent.value = null;
    inlineEditValue.value = '';
    toast.add({severity: 'success', summary: 'Gespeichert.', life: 3000});
  } catch (err) {
    inlineEditError.value = 'Speichern fehlgeschlagen.';
  } finally {
    inlineEditSaving.value = false;
  }
}

onMounted(() => {
  if (!statusFilter.value) statusFilter.value = 'bereit, gestartet, in Vorbereitung';
  loadCurrentSchoolYear();
  load();
});
</script>

<style scoped>
.table-header, .table-row {
  display: grid;
  align-items: center;
  gap: 4px;
  border-bottom: 1px solid var(--border);
  padding: 3px 2px;
  font-size: 14px;
  grid-template-columns: 110px minmax(100px, 1fr) minmax(80px, 1fr) 170px 130px 60px 120px 60px 180px;
}

header.page-header {
  border-bottom: 1px solid var(--border);
  padding-bottom: 8px;
  margin-bottom: 12px;
}

.row-actions {
  display: inline-flex;
  gap: 8px;
  justify-content: flex-end;
}

.row-actions .icon-btn[disabled],
.offer-card__actions .icon-btn[disabled] {
  opacity: 0.65;
  cursor: not-allowed;
  pointer-events: none;
  filter: grayscale(0.2);
}

/* Zeilen-Highlight bei Überschneidungen derselben Lehrkraft */
.row-conflict {
  background: rgba(239, 68, 68, 0.08); /* danger tint, gemäß Style-Guide */
  position: relative;
}

.row-conflict::before {
  content: "";
  position: absolute;
  left: -3px;
  top: 0;
  bottom: 0;
  width: 3px;
  background: var(--danger);
  border-radius: 0 2px 2px 0;
}

.muted {
  color: var(--muted);
}

.small {
  font-size: 12px;
  line-height: 16px;
}

.notice {
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 10px 12px;
}

.notice-danger {
  background: rgba(239, 68, 68, 0.1);
  border-color: #FCA5A5;
}

.mt-12 {
  margin-top: 12px;
}

.sortable {
  cursor: pointer;
  user-select: none;
}

.sortable:hover {
  text-decoration: underline;
}

.pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  border: 1px solid var(--border);
}

.pill-mandatory {
  background: rgba(239, 68, 68, 0.10); /* danger tint */
  border-color: #FCA5A5;
  color: var(--text);
}

.pill-optional {
  background: var(--bg);
  color: var(--muted);
}

.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid var(--border);
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.btn {
  padding: .35rem .6rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
}

.btn-primary {
  background: var(--primary);
  color: #fff;
  border-color: var(--primary);
}

.btn-warn {
  background: var(--warn);
}


/* Angebotstitel-Format nach Status */
.offer-title {
  font-weight: 700;
}

.offer-title--completed {
  font-weight: 500;
}

.offer-title--canceled {
  font-weight: 400;
  text-decoration: line-through;
}

/* Attendance-Button: wie Action-Buttons, mit pastellig grünem Hintergrund */
.icon-btn.attendance-btn {
  background: rgba(16, 185, 129, 0.12); /* success tint */
  border: 1px solid rgba(16, 185, 129, 0.35);
  color: var(--text);
  height: 30px;
  width: 30px;
  border-radius: 5px;
  justify-content: center;
}

.icon-btn.attendance-btn:hover {
  background: rgba(16, 185, 129, 0.18);
}

/* Viewports: Tablet und größer; <768px → horizontaler Scroll im Tabellenbereich */
.table {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

@media (max-width: 767px) {
  .offer-list-page {
    overflow-x: auto;
  }

  .table-header,
  .table-row {
    min-width: 1100px;
  }
}

/* Icon hinter Angebotstitel (auto-generiert) */
.offer-auto-icon {
  margin-left: 6px;
  color: var(--muted);
  font-size: 14px;
  vertical-align: middle;
}

/* Inline-Edit-Button für Angebotstitel/Thema (dezent, wirkt wie Text) */
.btn-inline-edit {
  border: 1px dashed var(--border);
  padding: 2px 4px;
  margin: -2px -4px;
  border-radius: 4px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: background-color 140ms cubic-bezier(0.2, 0, 0, 1),
  border-color 140ms cubic-bezier(0.2, 0, 0, 1),
  transform 80ms cubic-bezier(0.2, 0, 0, 1);
  background-color: var(--bg);
}

.btn-inline-edit--muted {
  color: var(--muted);
}

.btn-inline-edit:hover {
  border-style: solid;
  background-color: #e7e7e7;
}

.btn-inline-edit:active {
  transform: scale(0.97);
}

.btn-inline-edit:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 1px;
}

/* Feldnahe Hinweise im Dialog */
.help {
  margin-top: 4px;
  font-size: 12px;
  line-height: 16px;
  color: var(--muted);
}

.help.danger {
  color: var(--danger);
}

/* --- Filter-Layout: 2 Zeilen bei ≤1199px, flexibel bei Desktop --- */

.filters--offers {
  display: grid;
  grid-template-columns:  155px auto 180px 150px 70px 140px 70px;
  gap: 8px;
  align-items: end;
}

.filters__date-input {
  display: flex;
  align-items: center;
  gap: 4px;
}

.filters__field--date {
  min-width: 155px;
}

.offer-cards {
  display: none;
}

.offer-card {
  position: relative;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--surface);
  padding: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, .04);
  display: grid;
  gap: 10px;
  max-width: 100%;
  min-width: 0;
}

.offer-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.offer-card__time {
  font-size: 13px;
}

.offer-card__status {
  flex: 0 0 auto;
}

.offer-card__status-pill {
  display: inline-block;
  font-size: 12px;
  line-height: 16px;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid var(--border);
  width: max-content;
}

.offer-card__body {
  display: grid;
  gap: 6px;
}

.offer-card__title-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.offer-card__title {
  font-size: 14px;
  line-height: 1.3;
}

.offer-card__topic {
  margin: 0;
}

.offer-card__meta {
  margin: 0;
  padding: 0;
  display: grid;
  gap: 4px;
}

.offer-card__meta-row {
  display: inline-flex;
  gap: 8px;
  font-size: 13px;
  align-items: center;
}

.offer-card__meta-row dt {
  margin: 0;
}

.offer-card__meta-row dd {
  margin: 0;
}

.offer-card__meta-row button.attendance-btn {
  width: auto;
}

.offer-card__footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.offer-card__footer.space-between {
  justify-content: space-between;
  column-gap: 40px;
  flex-wrap: wrap;
}

.offer-card__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1 1 auto;
  justify-content: flex-end; /* Icon-Buttons nach rechts */
  margin-top: 10px;
}

.offer-card__actions .icon-btn {
  flex: 1 1 40px;
  min-width: 40px;
  max-width: 80px;
}

/* Konflikt-Markierung auch auf Cards sichtbar halten */
.offer-card.row-conflict {
  background: rgba(239, 68, 68, 0.08);
}

/* Sichtbarkeit je Viewport:
   ≥1200px: Tabelle
   ≤1199px: Cards */
@media (max-width: 1199px) {
  :global(.layout) {
    grid-template-columns: 240px 1200px !important;
  }

  .field.date-picker {
    width: auto;
  }

  .field label {
    margin-bottom: 0;
  }

  .filters--offers {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    grid-template-areas:
      "date     search   search   reset"
      "location teacher  mandatory status";
    gap: 8px 12px;
    align-items: end;
    border: 1px solid #ddd;
    border-radius: 8px;
    padding: 12px;
    background: whitesmoke;
    margin-bottom: 20px;
  }

  .filters__field--date {
    grid-area: date;
  }

  .filters__field--search {
    grid-area: search;
  }

  .filters__field--location {
    grid-area: location;
  }

  .filters__field--teacher {
    grid-area: teacher;
  }

  .filters__field--mandatory {
    grid-area: mandatory;
  }

  .filters__field--status {
    grid-area: status;
  }

  .filters__field--reset {
    grid-area: reset;
  }

  .filters__field--reset button {
    width: 100%;
  }

  .filters__reset-label {
    visibility: hidden;
  }

  .offer-table {
    display: none;
  }

  .offer-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(310px, 1fr));
    gap: 10px;
  }

  .table {
    overflow: visible;
  }

  .card {
    padding: 12px;
  }

  .pagination-row {
    font-size: 13px;
  }

  .field {
    margin-bottom: 0;
  }
}

/* Kleines Handy: einspaltig, trotzdem max. 2 Filter-Zeilen über Grid-Areas */
@media (max-width: 767px) {
  .filters--offers {
    grid-template-columns: repeat(3, 1fr);
    grid-template-areas:
      "date      search   search"
      "location  location teacher"
      "mandatory status   reset";
  }
}

@media (max-width: 460px) {
  .pagination-row .mobile-hide {
    display: none;
  }

  header.page-header .h1 {
    font-size: 22px;
  }
}

@media (max-width: 399px) {
  .filters--offers {
    grid-template-columns:1fr 1fr;
    grid-template-areas:
      "date      search"
      "location  teacher"
      "mandatory status"
      "reset     reset";
  }

  .filters__field--reset {
    margin-top: 10px;
  }

  header.page-header .h1 {
    font-size: 18px;
  }
}
</style>